import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import Constants from '../../utils/constants';
import { authHeader } from '../../utils/headers';
import { extractId } from '../../utils/apiResource';

// Dérive la base API à partir d'une URL constante (même pattern que clients.api.js / campaigns.api.js)
function getApiBase() {
    try {
        const u = new URL(Constants.API_ROUTE_PATRIMOINES);
        return `${u.protocol}//${u.host}`;
    } catch (e) {
        return import.meta.env.VITE_APIURL || '';
    }
}

// Extrait le path (sans host) d'une URL absolue — préserve le préfixe `/api`
// porté par les constantes (`APIURL` contient déjà `/api`).
function toPath(url) {
    try {
        const u = new URL(url);
        return u.pathname + u.search;
    } catch (e) {
        return url;
    }
}

export const patrimoinesApi = createApi({
    reducerPath: 'patrimoinesApi',
    baseQuery: fetchBaseQuery({
        baseUrl: getApiBase(),
        prepareHeaders: (headers) => {
            const token = authHeader();
            if (typeof token === 'string' && token) {
                headers.set('authorization', token);
            }
            return headers;
        },
    }),
    tagTypes: ['PatrimoineList', 'Patrimoine'],
    endpoints: (builder) => ({
        // Liste légère des patrimoines d'un client — utilisée pour le <Select> du step 2
        // du formulaire de campagne.
        // Backend : GET /api/patrimoines?client=:id&groups[]=patrimoine:list
        // Payload minimal : id, @id, libelle, isUnipatrimoine, nombreDeBacs, conteneursCount
        getPatrimoinesLightByClient: builder.query({
            query: (clientId) => {
                const id = extractId(clientId);
                return toPath(`${Constants.API_ROUTE_PATRIMOINES_CLIENT.replace(':id', id)}&groups[]=patrimoine:list`);
            },
            transformResponse: (response) => {
                if (response && Array.isArray(response['hydra:member'])) {
                    return response['hydra:member'];
                }
                return Array.isArray(response) ? response : [];
            },
            providesTags: (result, _err, clientId) => {
                const cid = extractId(clientId);
                return result
                    ? [
                        ...result.map((p) => ({ type: 'Patrimoine', id: p.id })),
                        { type: 'PatrimoineList', id: cid },
                    ]
                    : [{ type: 'PatrimoineList', id: cid }];
            },
        }),

        // Détail d'un patrimoine (avec ses conteneurs) — déclenché seulement quand
        // l'utilisateur sélectionne un patrimoine dans le <Select>.
        // Backend : GET /api/patrimoines/:id?groups[]=patrimoine:detail
        // Payload : champs de la liste + collection complète `conteneurs`
        // (id, @id, lat, lon, adresse, ordreCsv, nomenclature, etc.)
        getPatrimoineDetail: builder.query({
            query: (idOrIri) => {
                const id = extractId(idOrIri);
                return toPath(`${Constants.API_ROUTE_PATRIMOINE.replace(':id', id)}?groups[]=patrimoine:detail`);
            },
            providesTags: (result, _err, idOrIri) => [
                { type: 'Patrimoine', id: extractId(idOrIri) },
            ],
        }),

        // TOUR-02 — Mise à jour du plan de tournée (ordre / adresse / coords) depuis un CSV.
        // Backend : POST /api/patrimoines/:id/plan-tournee (PlanTourneeUpdateController, JSON simple)
        // Payload : { conteneurs: [{ nomenclature, ordreCsv, adresse?, latitude?, longitude? }] }
        // Réponse : { misAJour: n } · 422 { violations: [{ propertyPath, message }] }
        // Ne crée ni ne supprime jamais de conteneur (cadrage TOUR-02 R13/R14).
        updatePlanTournee: builder.mutation({
            query: ({ patrimoineId, conteneurs }) => ({
                url: toPath(`${Constants.API_ROUTE_PATRIMOINE.replace(':id', extractId(patrimoineId))}/plan-tournee`),
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ conteneurs }),
            }),
            invalidatesTags: (_res, _err, { patrimoineId }) => [
                { type: 'Patrimoine', id: extractId(patrimoineId) },
            ],
        }),
    }),
});

export const {
    useGetPatrimoinesLightByClientQuery,
    useLazyGetPatrimoinesLightByClientQuery,
    useGetPatrimoineDetailQuery,
    useUpdatePlanTourneeMutation,
    usePrefetch,
} = patrimoinesApi;

export { extractId };
